import lodash from 'lodash'
import types from '@/store/mutation-types'
import { getEnvSetting } from '@/api/apps/request'
import { getThreshold, getAllSettings, getRegionEnvsList } from '@/api/settings/request'
import { SETTING_CODE } from '@/api/settings/consts'

const defaultState = {
  allSettings: {},
  backEndEnv: {
    'ext.AD.mock': undefined,
    engineerEntry: undefined,
    vendorEntry: undefined,
    'security.login.force2FA': undefined,
    'security.login.authenticationCodeExpireSec': undefined,
  },
  redirectLinks: {
    'urls.officeManagementRegulations': undefined,
    'urls.retailManagementRegulations': undefined,
    'urls.greenFitoutGuide': undefined,
  },
  moduleConfig: {},
  jwt: null,
}

// getters
const getters = {
  setting: (state) => (code) => (state.allSettings[code]),
  moduleConfig: state => state.moduleConfig,
  backEndEnv: state => state.backEndEnv,
  jwt: state => state.jwt,
  isAdMock: state => state.backEndEnv['ext.AD.mock'],
  engineerEntry: state => state.backEndEnv.engineerEntry,
  vendorEntry: state => state.backEndEnv.vendorEntry,
  force2FA: state => state.backEndEnv['security.login.force2FA'],
  fileUploadSize: (state) => state.allSettings[SETTING_CODE.docUploadSize] || 10,
  redirectLinksList: state => Object
    .entries(state.redirectLinks)
    .filter(([code, link]) => link)
    .map(([code, link]) => ({ key: code.split('.')[1] || code, value: link })),
}

/**
 * State Actions
 */
const actions = {
  async loadAllSettingValues ({ state, commit }) {
    const settings = (await getAllSettings()).reduce((result, rec) => {
      result[rec.code] = rec.value
      return result
    }, {})
    commit(types.SETTING_LOAD_ALL_VALUE, { settings })
  },
  async loadBackEndEnv ({ state, commit }, envCodes) {
    let toLoad = envCodes.filter(code => {
      const value = lodash.get(state.backEndEnv, code)
      return typeof value === 'undefined'
    })

    if (toLoad.length < 1) return

    const envValues = await getEnvSetting(toLoad)
    if (!envValues) return

    Object.entries(envValues).forEach(([envCode, envValue]) => {
      commit(types.BACK_END_ENV_UPDATE, {envCode, envValue})
    })
  },
  async loadRedirectLinks ({ state, commit }) {
    let toLoad = Object.keys(state.redirectLinks).filter(code => {
      const value = lodash.get(state.redirectLinks, code)
      return typeof value === 'undefined'
    })

    if (toLoad.length < 1) return
    const envValues = await getRegionEnvsList(toLoad)
    if (!envValues) return

    Object.entries(envValues).forEach(([envCode, envValue]) => {
      commit(types.REDIRECT_LINK_UPDATE, {envCode, envValue})
    })
  },
  async getOrLoadValue ({ state, commit }, settingCode) {
    const settingValue = state.allSettings[settingCode]
    if (!settingValue) {
      const settingValue = await getThreshold(settingCode)
      commit(types.SETTING_LOAD_VALUE, { settingCode, settingValue })
    }
    return state.allSettings[settingCode]
  },
  async loadSettingValue ({ commit }, settingCode) {
    const settingValue = await getThreshold(settingCode)
    commit(types.SETTING_LOAD_VALUE, { settingCode, settingValue })
  },
}

/**
 * State Mutations
 */
const mutations = {
  [types.SETTING_LOAD_VALUE] (state, { settingCode, settingValue }) {
    state.allSettings[settingCode] = settingValue
  },
  [types.SETTING_LOAD_ALL_VALUE] (state, { settings }) {
    state.allSettings = settings
  },
  [types.BACK_END_ENV_UPDATE] (state, { envCode, envValue }) {
    state.backEndEnv[envCode] = envValue
  },
  [types.JSON_WEB_TOKEN] (state, value) {
    let token = value
    if (value.includes('Bearer')) {
      token = value.split(' ')[1]
    }
    state.jwt = token
  },
  [types.REDIRECT_LINK_UPDATE] (state, { envCode, envValue }) {
    state.redirectLinks[envCode] = envValue
  },
  [types.RESET_STATE] (state) {
    state.allSettings = {}
  },
  [types.LOGOUT_RESET_STATE] (state) {
    state.backEndEnv = {
      ...lodash.cloneDeep(defaultState.backEndEnv),
    }
    state.redirectLinks = {
      ...lodash.cloneDeep(defaultState.redirectLinks),
    }
    state.jwt = null
  },
}

export default {
  namespaced: true,
  state: lodash.cloneDeep(defaultState),
  getters,
  actions,
  mutations,
}
